// The pickers' vendors from the connections directory (Connections "one
// list", P1): ONE vendor per service the server joined (a connection, a
// data source, a REST API and an MCP server from before), with the names
// each side answers to, so a workflow or a job that names any of them
// finds the one row. Where the directory is not there (switched off, an
// older api) the vendors come from vendorModel.vendorsOf as before and are
// widened here (`fromVendor`) to the same shape.
//
//   directoryVendors  one WorkspaceVendor per directory entry: its data side
//                     the data source member, its way in for tools the
//                     connection (else the REST API, else the MCP server)
//   fromVendor        a vendorsOf vendor in the same shape (the fallback)
//   namesOf           every name one side answers to (a plain Vendor: its own)
//   givesWords        what something given does, in the pickers' words
//                     ("Looks things up, pulls into the run"); never "Data",
//                     "Tools" or "Data + Tools"
//   pickDetail        a picker item's second line: its kind word and problem
//   sourceTitles      a run's "where the data is": each data source named as
//                     its service is (EODHD, not "EODHD market data API")
//   namedAsServices   vendors built in the browser renamed as their services
//                     are (a read-only line that keeps its own holdings rule)
//
// Pure: directoryVendors.selftest.ts (runs with `npm run check:format`).

import type { DirectoryEntry } from "@/api/connectionsDirectory";
import type { DataSourceKind } from "@/api/dataSources";
import type { Ability, Vendor } from "@/features/subagents/page/pageTypes";

/** A vendor with what the directory knows of it (or what can be worked out without it). */
export interface WorkspaceVendor extends Vendor {
  /** Every name its data side answers to (the data source's). */
  dataNames: string[];
  /** Every name its tools side answers to, its way in first (then a REST API or MCP server it took over). */
  toolNames: string[];
  /** Its tools' records, the way in first: the connection, else the REST API, then the MCP server from before. */
  ways: { source: "connection" | "rest" | "mcp"; id: string }[];
  /** "Database", "MCP server", "API", "File", "Files", "Python library"; "" for a catalogue product. */
  kindWord: string;
  /** The directory's sentence ("Agents look things up and pull data into their runs."); "" without it. */
  sentence: string;
  /** Some of its tools change things there. */
  writes: boolean;
  /** Its state as a dot: working, being looked into, or a problem; null when nothing checks it. */
  tone: "success" | "live" | "danger" | null;
  /** The directory entry's id; null for a vendor built without the directory. */
  entryId: string | null;
}

const DB_KINDS = new Set<string>(["postgres", "mysql", "clickhouse"]);
/** Kinds whose data is tables agents look things up in (directory QUERY_KINDS). */
const QUERY_KINDS = new Set<string>([...DB_KINDS, "sqlite", "duckdb", "file", "url", "seafile"]);
const DATA_WORD: Record<string, string> = { sqlite: "File", duckdb: "File", file: "File", url: "File", seafile: "Files", http_api: "API", python: "Python library" };

const add = (out: string[], name: string | null | undefined) => {
  if (name && !out.includes(name)) out.push(name);
};

function toneOf(status: string, problem: string): WorkspaceVendor["tone"] {
  if (problem) return "danger";
  if (status === "ok") return "success";
  return status === "discovering" ? "live" : null;
}

/** One vendor per directory entry, in the directory's order (problems first, then by name). */
export function directoryVendors(entries: readonly DirectoryEntry[]): WorkspaceVendor[] {
  return entries.map((e) => {
    const members = e.members ?? [];
    const data = members.find((m) => m.side === "data" && m.store === "data") ?? null;
    const ways = (["connection", "rest", "mcp"] as const).flatMap((store) => members.filter((m) => m.store === store).map((m) => ({ source: store, id: m.name })));
    const tool = ways[0] ?? null;
    const dataNames: string[] = [];
    add(dataNames, data?.name);
    const toolNames: string[] = [];
    for (const w of ways) add(toolNames, w.id);
    // The REST names a connection took over answer for its tools too.
    if (tool) for (const n of e.names ?? []) if (n !== data?.name && n !== data?.title) add(toolNames, n);
    const abilities: Ability[] = [];
    if (data) abilities.push("data");
    if (tool) abilities.push("tools");
    const key = tool ? `${tool.source}:${tool.id}` : data ? `data:${data.name}` : `entry:${e.id}`;
    return {
      key,
      name: e.name,
      data: data?.name ?? null,
      tool,
      abilities,
      problem: e.problem ?? "",
      sourceKind: data ? (data.kind as DataSourceKind) : null,
      dataNames,
      toolNames,
      ways,
      kindWord: e.kind_word ?? "",
      sentence: e.sentence ?? "",
      writes: !!e.abilities?.writes,
      tone: toneOf(e.status, e.problem ?? ""),
      entryId: e.id,
    };
  });
}

/** A kind word worked out without the directory (its rule: a database first, then an MCP server, a data kind, else an API). */
function kindWordOf(v: Vendor): string {
  const k = v.sourceKind ?? "";
  if (DB_KINDS.has(k)) return "Database";
  if (v.tool?.source === "mcp") return "MCP server";
  if (DATA_WORD[k]) return DATA_WORD[k];
  if (v.tool?.source === "rest") return "API";
  return "";
}

/** A vendor built without the directory (vendorModel.vendorsOf), in the same shape. */
export function fromVendor(v: Vendor): WorkspaceVendor {
  const w = v as Partial<WorkspaceVendor>;
  if (Array.isArray(w.dataNames) && Array.isArray(w.toolNames)) return v as WorkspaceVendor;
  return {
    ...v,
    dataNames: v.data ? [v.data] : [],
    toolNames: v.tool ? [v.tool.id] : [],
    ways: v.tool ? [v.tool] : [],
    kindWord: kindWordOf(v),
    sentence: "",
    writes: false,
    tone: v.problem ? "danger" : null,
    entryId: null,
  };
}

/** Every name one side of a vendor answers to (a plain Vendor: its own one). */
export function namesOf(v: Vendor, side: Ability): string[] {
  const w = v as Partial<WorkspaceVendor>;
  if (side === "data") return Array.isArray(w.dataNames) ? w.dataNames : v.data ? [v.data] : [];
  return Array.isArray(w.toolNames) ? w.toolNames : v.tool ? [v.tool.id] : [];
}

/** Its data is tables agents look things up in (a database, a file), not something pulled whole. */
export const queried = (v: Pick<Vendor, "sourceKind">) => QUERY_KINDS.has(v.sourceKind ?? "");

/**
 * What a service does with what is given of it, in the pickers' words:
 * "Looks things up, pulls into the run" (both), "Looks things up in its
 * tables" (a database or a file), "Pulls into the run" (an API's or a
 * library's data), "Looks things up" (its tools; ", makes changes" when
 * some write). `where` = whose runs: a workflow's ("the run") or a
 * subagent's ("its runs"). "" for nothing.
 */
export function givesWords(gives: readonly Ability[], v: Vendor, where: "the run" | "its runs" = "the run"): string {
  const data = gives.includes("data");
  const tools = gives.includes("tools");
  if (data && tools) return `Looks things up, pulls into ${where}`;
  if (data) return queried(v) ? "Looks things up in its tables" : `Pulls into ${where}`;
  if (tools) return (v as Partial<WorkspaceVendor>).writes ? "Looks things up, makes changes" : "Looks things up";
  return "";
}

/** A picker item's second line: its kind word, then its problem ("Database · Unreachable"); "" for a catalogue product with none. */
export function pickDetail(v: Vendor): string {
  return [fromVendor(v).kindWord, v.problem].filter(Boolean).join(" · ");
}

/**
 * The choices of a run's "where the data is" (Connections on): every data
 * source, each named as its service is called (the data source's own title
 * where the directory does not list it), by name. The value stays the
 * data source's name.
 */
export function sourceTitles(sources: readonly { name: string; title?: string | null }[], entries: readonly DirectoryEntry[]): { name: string; title: string }[] {
  const byData = new Map<string, string>();
  for (const e of entries) for (const m of e.members ?? []) if (m.store === "data" && !byData.has(m.name)) byData.set(m.name, e.name);
  return sources
    .map((s) => ({ name: s.name, title: byData.get(s.name) || s.title || s.name }))
    .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }) || a.name.localeCompare(b.name));
}

/**
 * Vendors built in the browser (vendorsOf), each named as the directory
 * names its service (`<store>:<name>` of any member it is), so a line that
 * keeps the browser's holdings rule still says "EODHD" where the pickers do.
 */
export function namedAsServices<V extends Vendor>(vendors: readonly V[], entries: readonly DirectoryEntry[]): V[] {
  const byMember = new Map<string, string>();
  for (const e of entries) for (const m of e.members ?? []) byMember.set(`${m.store}:${m.name}`, e.name);
  return vendors.map((v) => {
    const name = byMember.get(v.key) ?? (v.data ? byMember.get(`data:${v.data}`) : undefined);
    return name && name !== v.name ? { ...v, name } : v;
  });
}
